import React, { useState, useEffect, useRef } from 'react';

interface TextTypeProps {
  text: string;
  className?: string;
  duration?: number;
  delay?: number;
}

const TextType: React.FC<TextTypeProps> = ({ 
  text, 
  className = '', 
  duration = 50,
  delay = 0
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showArrow, setShowArrow] = useState(false);
  const hasAnimatedRef = useRef(false);
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let arrowTimeoutId: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;
          let currentIndex = 0;
          
          timeoutId = setTimeout(() => {
            intervalId = setInterval(() => {
              if (currentIndex <= text.length) {
                setDisplayedText(text.slice(0, currentIndex));
                currentIndex++;
              } else {
                if (intervalId) clearInterval(intervalId);
                // Show circle immediately after typing is complete
                setShowArrow(true);
              }
            }, duration);
          }, delay);
        }
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      if (arrowTimeoutId) clearTimeout(arrowTimeoutId);
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [text, duration, delay]);

  return (
    <h4 ref={ref} className={`${className} ${showArrow ? 'with-circle' : ''}`} style={{ position: 'relative' }}>
      <span style={{ visibility: 'hidden' }}>
        {text}
      </span>
      <span style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
        {displayedText}
        {!hasAnimatedRef.current || displayedText.length < text.length ? (
          <span className="typing-cursor">|</span>
        ) : null}
      </span>
      {showArrow && (
        <svg className="sketchy-circle" viewBox="0 0 100 50" preserveAspectRatio="none">
          <path
            d="M96,24 C95,11 88,5 75,3 C62,1 48,0.5 35,2 C22,3.5 10,7 5,14 C2,18 1,22 2,26 C3,32 6,38 12,42 C20,47 32,49 45,48.5 C58,48 72,46 82,42 C89,39 94,35 97,29 C99,25 99,20 97.5,16 C96.5,13 95.5,11 94,9.5 C93,8.5 92,8 92,8"
            fill="none"
            stroke="#ffbb00a2"
            strokeWidth="0.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="circle-path"
          />
        </svg>
      )}
    </h4>
  );
};

export default TextType;
